import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MerchantOrg,
  OnboardingStateResponse,
  OnboardingStep,
  VerifyMerchantBody,
  VerifyMerchantResult,
} from '@kindred/types';
import { api } from '../../lib/index.js';

/**
 * Onboarding (Module 2) query/mutation hooks. Server state via TanStack Query
 * over the cookie-session `api` client (CONTRACTS §5). The wizard reads the
 * per-step ladder and advances steps; both mutations write the fresh ladder
 * back into the cache so the StepRail updates without a refetch.
 */
export const onboardingKeys = {
  all: ['onboarding'] as const,
  state: () => [...onboardingKeys.all, 'state'] as const,
};

/** GET /onboarding — per-step states (FR-ONB-06). */
export function useOnboarding() {
  return useQuery({
    queryKey: onboardingKeys.state(),
    queryFn: () => api.get<OnboardingStateResponse>('/onboarding'),
  });
}

/**
 * GET /org — the active org, used to hydrate step 1 ("Add your store") so a page
 * refresh keeps the domains / business name the merchant already saved
 * (FR-ONB-01). Shares the ['org'] cache key with Settings' `useOrg`.
 */
export function useActiveOrg() {
  return useQuery({
    queryKey: ['org'] as const,
    queryFn: () => api.get<MerchantOrg>('/org'),
  });
}

/** POST /onboarding/verify-merchant (FR-ONB-01). Refreshes the ladder on success. */
export function useVerifyMerchant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VerifyMerchantBody) =>
      api.post<VerifyMerchantResult>('/onboarding/verify-merchant', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: onboardingKeys.state() });
      // Also refresh the org so revisiting step 1 re-hydrates the saved name/domains.
      qc.invalidateQueries({ queryKey: ['org'] as const });
    },
  });
}

/** POST /onboarding/steps/:step/complete — advance a step (FR-ONB-06). */
export function useCompleteStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (step: OnboardingStep) =>
      api.post<OnboardingStateResponse>(`/onboarding/steps/${step}/complete`),
    onSuccess: (next) => qc.setQueryData(onboardingKeys.state(), next),
  });
}
